import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TableRow, TableCell, Switch, Checkbox, Chip, CircularProgress, Snackbar, Alert, Tooltip } from '@mui/material';
import CustomButton from '../reusable/CustomButton';
import CustomTable, { TableHeaderData } from '../reusable/CustomTable';
import Panel from '../reusable/Panel';
import Loader from '../reusable/Loader';
import InlineEditText from '../reusable/InlineEditText';
import useUserDetails from '../hooks/useUserDetails';
import useErrorModal from '../hooks/useErrorModal';
import { getSchoolsConfig, patchSchoolConfig } from '../../services/eoaConfigServices';
import { SchoolConfigEntry } from '../../interfaces/EOAInterface';
import GoBackIcon from '../assets/transcript-selection-arrow-left.svg';
import './SchoolsConfigPage.css';

const sortSchools = (list: SchoolConfigEntry[]): SchoolConfigEntry[] =>
  [...list].sort((a, b) => {
    if (a.is_primary_on_eoa !== b.is_primary_on_eoa) return a.is_primary_on_eoa ? -1 : 1;
    return a.school_name.localeCompare(b.school_name);
  });

type ToastState = {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
};

const PATCHABLE_BOOLEAN_FIELDS: Record<string, string> = {
  has_cte_courses: '/has_cte_courses',
  has_detailed_cte_clusters: '/has_detailed_cte_clusters',
  required_sat_for_graduation: '/required_sat_for_graduation',
  required_act_for_graduation: '/required_act_for_graduation',
};

const tableHeaders: TableHeaderData<SchoolConfigEntry>[] = [
  { key: 'school_name', label: 'School Name' },
  { key: 'is_primary_on_eoa', label: 'Primary on EOA' },
  { key: 'num_students', label: 'Students #' },
  { key: 'num_school_staff', label: 'Staff #' },
  { key: 'num_families', label: 'Families #' },
  { key: 'test_types', label: 'Test Types' },
  { key: 'terms', label: 'Terms' },
  { key: 'required_sat_for_graduation', label: 'SAT Required' },
  { key: 'required_act_for_graduation', label: 'ACT Required' },
  { key: 'has_cte_courses', label: 'CTE Courses' },
  { key: 'has_detailed_cte_clusters', label: 'Detailed CTE' },
  { key: 'career_high_wage_threshold', label: 'Wage Threshold' },
  { key: 'career_high_growth_threshold', label: 'Growth Threshold' },
  { key: 'regional_area_type', label: 'Region Type' },
  { key: 'regional_area_title', label: 'Region Title' },
  { key: 'survey_dashboard_url', label: 'Survey Dashboard URL' },
  { key: 'survey_questions_url', label: 'Survey Questions URL' },
];

const SchoolsConfigPage = () => {
  const navigate = useNavigate();
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const [schools, setSchools] = useState<SchoolConfigEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingFields, setSavingFields] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'success' });

  const fetchSchoolsConfig = useCallback(async () => {
    if (!userDetails) return;
    try {
      setLoading(true);
      const response = await getSchoolsConfig(userDetails.eoaId, userDetails.access_token);
      setSchools(sortSchools(response.data));
    } catch (error) {
      handleOpenErrorModal('Error fetching schools configuration', error as Error);
    } finally {
      setLoading(false);
    }
  }, [userDetails, handleOpenErrorModal]);

  useEffect(() => {
    fetchSchoolsConfig();
  }, [fetchSchoolsConfig]);

  const markFieldSaving = (key: string) => {
    setSavingFields((prev) => new Set(prev).add(key));
  };

  const unmarkFieldSaving = (key: string) => {
    setSavingFields((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const applyPatch = async (schoolId: string, path: string, value: unknown): Promise<boolean> => {
    if (!userDetails) return false;
    try {
      const response = await patchSchoolConfig(userDetails.eoaId, schoolId, userDetails.access_token, [
        { op: 'replace', path, value },
      ]);
      setSchools((prev) => sortSchools(prev.map((s) => (s.school_id === schoolId ? response.data : s))));
      return true;
    } catch (error) {
      handleOpenErrorModal('Error saving school configuration', error as Error);
      return false;
    }
  };

  const handleBooleanToggle = async (schoolId: string, fieldKey: string, value: boolean) => {
    const path = PATCHABLE_BOOLEAN_FIELDS[fieldKey];
    if (!path) return;

    const savingKey = `${schoolId}:${fieldKey}`;
    markFieldSaving(savingKey);

    // Optimistically update local state so the switch snaps immediately
    setSchools((prev) => prev.map((s) => (s.school_id === schoolId ? { ...s, [fieldKey]: value } : s)));

    const success = await applyPatch(schoolId, path, value);
    unmarkFieldSaving(savingKey);

    if (!success) {
      // Revert optimistic update
      setSchools((prev) => prev.map((s) => (s.school_id === schoolId ? { ...s, [fieldKey]: !value } : s)));
      setToast({ open: true, message: 'Failed to save — your change has been reverted.', severity: 'error' });
    }
  };

  const handleNumberSave = async (schoolId: string, path: string, rawValue: string): Promise<boolean> => {
    const parsed = parseFloat(rawValue);
    if (isNaN(parsed) || parsed < 0) return false;
    return applyPatch(schoolId, path, parsed);
  };

  const handleStringSave = async (
    schoolId: string,
    path: string,
    value: string,
    nullable: boolean
  ): Promise<boolean> => {
    if (nullable && value === '') {
      if (!userDetails) return false;
      try {
        const response = await patchSchoolConfig(userDetails.eoaId, schoolId, userDetails.access_token, [
          { op: 'remove', path },
        ]);
        setSchools((prev) => sortSchools(prev.map((s) => (s.school_id === schoolId ? response.data : s))));
        return true;
      } catch (error) {
        handleOpenErrorModal('Error saving school configuration', error as Error);
        return false;
      }
    }
    return applyPatch(schoolId, path, value);
  };

  const renderBooleanCell = (school: SchoolConfigEntry, fieldKey: keyof SchoolConfigEntry) => {
    const savingKey = `${school.school_id}:${fieldKey}`;
    const isSaving = savingFields.has(savingKey);
    return (
      <span className="schools-config-bool-cell">
        <Switch
          checked={Boolean(school[fieldKey])}
          onChange={(e) => handleBooleanToggle(school.school_id, fieldKey, e.target.checked)}
          disabled={isSaving}
          size="small"
        />
        {isSaving && <CircularProgress size={14} className="schools-config-bool-spinner" />}
      </span>
    );
  };

  const renderChipsCell = (items: string[] | null) => (
    <span className="schools-config-chips">
      {!items || items.length === 0 ? (
        <span className="schools-config-empty">—</span>
      ) : (
        items.map((item) => <Chip key={item} label={item} size="small" />)
      )}
    </span>
  );

  const renderNumberCell = (school: SchoolConfigEntry, fieldKey: keyof SchoolConfigEntry, path: string) => (
    <InlineEditText
      text={String(school[fieldKey] ?? '')}
      onSave={(val) => handleNumberSave(school.school_id, path, val)}
      className="schools-config-inline-number"
      compact
    />
  );

  const renderStringCell = (
    school: SchoolConfigEntry,
    fieldKey: keyof SchoolConfigEntry,
    path: string,
    nullable = false
  ) => {
    const value = school[fieldKey];
    const text = value == null ? '' : String(value);
    return (
      <InlineEditText
        text={text}
        onSave={(val) => handleStringSave(school.school_id, path, val, nullable)}
        className="schools-config-inline-text"
        compact
      />
    );
  };

  const renderUrlCell = (school: SchoolConfigEntry, fieldKey: keyof SchoolConfigEntry, path: string) => {
    const value = school[fieldKey];
    const text = value == null ? '' : String(value);
    return (
      <span className="schools-config-url-cell">
        {text && (
          <Tooltip title={text} placement="top">
            <a href={text} target="_blank" rel="noopener noreferrer" className="schools-config-url-link">
              View
            </a>
          </Tooltip>
        )}
        <InlineEditText
          text={text}
          onSave={(val) => handleStringSave(school.school_id, path, val, true)}
          className="schools-config-inline-text"
          compact
        />
      </span>
    );
  };

  if (!userDetails || loading) return <Loader />;

  return (
    <div className="main-content">
      <CustomButton
        variant="text"
        buttonText="Back"
        icon={<img src={GoBackIcon} alt="go back" />}
        iconPosition="start"
        buttonAction={() => navigate('/overview')}
      />
      <div className="schools-config-header">
        <h1 className="schools-config-page-title">Schools Configuration</h1>
      </div>

      <Panel className="schools-config-panel">
        <CustomTable tableHeaderData={tableHeaders} size="small" headerTooltip>
          {schools.map((school) => (
            <TableRow key={school.school_id}>
              <TableCell>
                <span className="schools-config-school-name">{school.school_name}</span>
              </TableCell>
              <TableCell>
                <Checkbox checked={school.is_primary_on_eoa} disabled size="small" />
              </TableCell>
              <TableCell>{renderNumberCell(school, 'num_students', '/num_students')}</TableCell>
              <TableCell>{renderNumberCell(school, 'num_school_staff', '/num_school_staff')}</TableCell>
              <TableCell>{renderNumberCell(school, 'num_families', '/num_families')}</TableCell>
              <TableCell>{renderChipsCell(school.test_types)}</TableCell>
              <TableCell>{renderChipsCell(school.terms)}</TableCell>
              <TableCell>{renderBooleanCell(school, 'required_sat_for_graduation')}</TableCell>
              <TableCell>{renderBooleanCell(school, 'required_act_for_graduation')}</TableCell>
              <TableCell>{renderBooleanCell(school, 'has_cte_courses')}</TableCell>
              <TableCell>{renderBooleanCell(school, 'has_detailed_cte_clusters')}</TableCell>
              <TableCell>
                {renderNumberCell(school, 'career_high_wage_threshold', '/career_high_wage_threshold')}
              </TableCell>
              <TableCell>
                {renderNumberCell(school, 'career_high_growth_threshold', '/career_high_growth_threshold')}
              </TableCell>
              <TableCell>{renderStringCell(school, 'regional_area_type', '/regional_area_type', true)}</TableCell>
              <TableCell>{renderStringCell(school, 'regional_area_title', '/regional_area_title', true)}</TableCell>
              <TableCell>{renderUrlCell(school, 'survey_dashboard_url', '/survey_dashboard_url')}</TableCell>
              <TableCell>{renderUrlCell(school, 'survey_questions_url', '/survey_questions_url')}</TableCell>
            </TableRow>
          ))}
        </CustomTable>
        {schools.length === 0 && <p className="schools-config-empty-state">No schools found for this EOA.</p>}
      </Panel>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default SchoolsConfigPage;
